import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, HStack, Separator, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { subMonths } from 'date-fns';

import { GradientIconBadge } from '~/b4b/components/gradient-icon-badge/GradientIconBadge';
import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import articleIcon from '~/b4b/images/b4b-resources-icon-article.svg';
import guideIcon from '~/b4b/images/b4b-resources-icon-guide.svg';
import hubIcon from '~/b4b/images/b4b-resources-icon-hub.svg';
import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { ResourcesDocument } from './graphql/query.resources.generated';
import { LoadingResourcesCard } from './LoadingResourcesCard';

const resourceLinkStyles = css.raw({
  color: 'inherit',
  _hover: {
    color: 'inherit',
    textDecoration: 'none',
    '& .underlineOnHover': { textDecoration: 'underline' },
  },
});

type ResourceItemProps = {
  icon: string;
  label: string;
  title: string;
  to: string;
  eventName: string;
};

const ResourceItem = ({ icon, label, title, to, eventName }: ResourceItemProps) => (
  <SambaAutoLink
    to={to}
    target="_blank"
    rel="noopener noreferrer"
    data-track-event="ElementClicked"
    data-track-name={eventName}
    data-track-module="Resources"
    data-track-flow="HomeLanding"
    css={resourceLinkStyles}
  >
    <HStack css={{ gap: 'space.200', alignItems: 'center' }}>
      <GradientIconBadge>
        <img src={icon} alt="" />
      </GradientIconBadge>
      <VStack css={{ gap: 'space.0' }}>
        <Text variant="text25" color="text.weak">
          {label}
        </Text>
        <Text className="underlineOnHover" variant="text100">
          {title}
        </Text>
      </VStack>
    </HStack>
  </SambaAutoLink>
);

const ActiveResources = () => (
  <Box css={{ display: 'grid', gridTemplateColumns: { base: '1fr', lg: '1fr 1fr' }, gap: 'space.300' }}>
    <ResourceItem
      icon={hubIcon}
      label="Educational hub"
      title="General resources"
      to={urlBuilder.getResources()}
      eventName="PSGeneralResourcesHub"
    />
    <ResourceItem
      icon={guideIcon}
      label="Guide"
      title="401(k) compliance cycle"
      to={urlBuilder.getComplianceCycleGuide()}
      eventName="PSComplianceCycleGuide"
    />
    <ResourceItem
      icon={articleIcon}
      label="Article"
      title="Employee engagement starter kit"
      to={urlBuilder.getEngagementStarterKit()}
      eventName="PSEngagementArticle"
    />
    <ResourceItem
      icon={articleIcon}
      label="Article"
      title="Fiduciary responsibilities"
      to={urlBuilder.getFiduciaryResponsibilitiesGuide()}
      eventName="PSFiduciaryResponsibilitiesArticle"
    />
  </Box>
);

const OnboardingResources = () => (
  <Box css={{ display: 'grid', gridTemplateColumns: { base: '1fr', lg: '1fr 1fr' }, gap: 'space.300' }}>
    <ResourceItem
      icon={hubIcon}
      label="Educational hub"
      title="Onboarding help center"
      to={urlBuilder.getOnboardingHelpCenter()}
      eventName="PSOnboardingHub"
    />
    <ResourceItem
      icon={hubIcon}
      label="Educational hub"
      title="Managing your 401(k) plan"
      to={urlBuilder.getManageYour401kPlan()}
      eventName="PSManagingPlanHub"
    />
    <ResourceItem
      icon={articleIcon}
      label="Article"
      title="401(k) glossary"
      to={urlBuilder.get401kGlossary()}
      eventName="PS401KGlossaryArticle"
    />
    <ResourceItem
      icon={guideIcon}
      label="Guide"
      title="Reports overview"
      to={urlBuilder.getReportsOverviewGuide()}
      eventName="PSReportsGuide"
    />
    <ResourceItem
      icon={guideIcon}
      label="Guide"
      title="Employee engagement playbook"
      to={urlBuilder.getEmployeeEngagementPlaybook()}
      eventName="PSEngagementGuide"
    />
  </Box>
);

export const ResourcesCard = () => {
  const employerId = useCurrentEmployerId();
  const { data } = useGraphQLQuery(ResourcesDocument, {
    variables: { employerId },
  });

  const startOn = data?.employer?.plan?.startOn;
  const isRecentlyStarted = startOn ? new Date(startOn) > subMonths(new Date(), 6) : true;
  const isLoading = !data;

  return (
    <Card
      css={{ p: 'space.300' }}
      data-track-event="ElementViewed"
      data-track-name="ResourcesCard"
      data-track-flow="HomeLanding"
      data-track-module="Resources"
    >
      <HStack css={{ pb: 'space.300', justifyContent: 'space-between', alignItems: 'center' }}>
        <H2 variant="title200">Resources for you</H2>
        <Box css={{ display: { base: 'none', lg: 'block' } }}>
          <SambaAutoLink
            to={urlBuilder.resources()}
            data-track-event="ElementClicked"
            data-track-name="ViewAllResources"
            data-track-flow="HomeLanding"
            data-track-module="Resources"
          >
            View all
          </SambaAutoLink>
        </Box>
      </HStack>

      <Separator />

      <Box css={{ pt: 'space.300' }}>
        {isLoading && <LoadingResourcesCard />}
        {!isLoading && isRecentlyStarted && <OnboardingResources />}
        {!isLoading && !isRecentlyStarted && <ActiveResources />}
      </Box>

      <Box css={{ display: { base: 'block', lg: 'none' }, pt: 'space.300' }}>
        <Separator />
        <Box css={{ pt: 'space.300' }}>
          <SambaAutoLink
            to={urlBuilder.resources()}
            data-track-event="ElementClicked"
            data-track-name="ViewAllResources"
            data-track-flow="HomeLanding"
            data-track-module="Resources"
          >
            View all
          </SambaAutoLink>
        </Box>
      </Box>
    </Card>
  );
};
